import { getCurrentInstance } from 'vue-demi';

import { IAgent, Contact } from '@/types';
import { useCommonProps } from './use-common-props';
import { getAgentName, getContactName } from '@/common';
import {
  ConferenceMember,
  CALL_MEMBER_TYPE,
  CALL_MEMBER_STATUS,
} from '../types';
import {
  CALL_MEMBER_TYPE_LABEL,
  CALL_MEMBER_STATUS_LABEL,
  CALL_MEMBER_STATUS_COLOR,
} from '../constants';

export function useDialerMembers() {
  const { getAgentById, getContactById } = useCommonProps();

  const vm = getCurrentInstance()!;
  const $t = vm.proxy.$t.bind(vm.proxy);

  const CALL_MEMBER_ACTIONS = [
    {
      key: 'hang-up',
      label: $t('hitl.modules.dialer.actions.hang-up'),
      icon: 'call_end',
      color: 'error',
    },
  ];

  const getPerson = (member: ConferenceMember) => {
    const { memberId, type, identifier } = member;
    switch (type) {
      case CALL_MEMBER_TYPE.AGENT:
        return getAgentById.value(memberId);
      case CALL_MEMBER_TYPE.CONTACT:
        return getContactById.value(memberId)!;
      default:
        return {
          name: identifier,
          contactId: memberId,
        };
    }
  };

  const getMemberName = (member: ConferenceMember) => {
    const person = getPerson(member);
    const isAgent = member.type === CALL_MEMBER_TYPE.AGENT;

    if (!isAgent) {
      return getContactName(person as Contact, $t);
    }

    return person
      ? getAgentName(person as IAgent, $t)
      : $t('hitl.common.agentLoadingLabel');
  };

  const getMemberIdentifierLabel = (member: ConferenceMember) => {
    const type = $t(CALL_MEMBER_TYPE_LABEL[member.type]);
    return `${type}: ${member.identifier}`;
  };

  const getMemberStatusLabel = (member: ConferenceMember) => {
    return $t(CALL_MEMBER_STATUS_LABEL[member.status]);
  };

  const getMemberStatusType = (member: ConferenceMember) => {
    return CALL_MEMBER_STATUS_COLOR[member.status];
  };

  const showDisconnectOption = (member: ConferenceMember) => {
    const isAgent = member.type === CALL_MEMBER_TYPE.AGENT;
    const isThirdParty = member.type === CALL_MEMBER_TYPE.THIRD_PARTY;
    const CAN_DISCONNECT_STATUS_LIST = [
      CALL_MEMBER_STATUS.RINGING,
      CALL_MEMBER_STATUS.JOINED,
      CALL_MEMBER_STATUS.CONNECTED,
      CALL_MEMBER_STATUS.ON_HOLD,
      CALL_MEMBER_STATUS.MUTED,
      CALL_MEMBER_STATUS.RINGING,
    ];

    const canDisconnect = CAN_DISCONNECT_STATUS_LIST.includes(member.status);
    return canDisconnect && (isAgent || isThirdParty);
  };

  const getMemberOptions = (member: ConferenceMember) => {
    return showDisconnectOption(member) ? CALL_MEMBER_ACTIONS : [];
  };

  const onActionClick = async (key: string, member: ConferenceMember) => {
    if (key === 'hang-up') {
      vm.proxy.$emit('hang-up', member.memberId);
    }
  };

  return {
    getPerson,
    getMemberName,
    getMemberIdentifierLabel,
    getMemberStatusLabel,
    getMemberStatusType,
    getMemberOptions,
    onActionClick,
    CALL_MEMBER_ACTIONS,
  };
}
